import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form4868Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 4868 Instructions: Tax Extension Guidelines"
          description="Learn how to request a tax extension with Form 4868. Follow our comprehensive instructions for filing and extending your tax deadline."
          keywords="form 4868 instructions, irs form 4868 instructions, personal tax extension, instructions for form 4868, IRS tax extension form 4868, form 4868 filing instructions, Form 1040 extension"
          canonical="resources/tax-extension/form-4868-instructions/"
          ogType="article"
          jsonLdPricing="11.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          {' '}
          <span className="highlight-container">
            <span className="highlight"> Form 4868</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-file your IRS Form 4868 for individuals to request an automatic
          extension for your 2025 tax return.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868instructions`}
          >
            Get an Extension Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-4868-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 4868 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Form 4868</span> is
                        filed by individual taxpayers with the IRS to request an
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          automatic <br /> 6-month extension of time
                        </span>{' '}
                        to file their federal income tax return, including{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          Forms 1040, 1040-SR, 1040-NR, and 1040-SS
                        </span>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <Link
                          href="/resources/tax-extension/form-4868-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>
                        : Form 4868 must be filed on or before the original tax
                        return due date of <br />
                        <span style={{ fontWeight: 600 }}>April 15</span>. For
                        the 2025 tax year, filing Form 4868 extends the filing
                        deadline to <br />
                        <span style={{ fontWeight: 600 }}>
                          October 15, 2026
                        </span>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Penalties:</span>{' '}
                        Late filing penalty of{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          5% per month (up to 25%);
                        </span>{' '}
                        late payment penalty ranges of{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          0.5% per month (up to 25%)
                        </span>
                        and applicable interest.
                      </span>
                    </li>
                  </Box>
                </Box>
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 4868?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 1 }}
                >
                  <Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <Link
                        href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        IRS Form 4868
                      </Link>
                      ,{' '}
                      <span style={{ fontWeight: 600 }}>
                        Application for Automatic Extension of Time to File U.S.
                        Individual Income Tax Return
                      </span>
                      , allows individual taxpayers to request additional time
                      to file their income tax return. This extension applies to
                      U.S. citizens and residents who need more time to complete
                      their{' '}
                      <span style={{ fontWeight: 600 }}>
                        Form 1040, 1040-SR, 1040-NR, or 1040-SS
                      </span>
                      . Filing{' '}
                      <span style={{ fontWeight: 600 }}>Form 4868</span> grants
                      a 6-month extension for filing tax returns.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      For U.S. citizens or residents who are out of the country
                      on the regular due date, the IRS allows{' '}
                      <span style={{ fontWeight: 600 }}>
                        an additional 2 months
                      </span>{' '}
                      to file their return and pay any amount due{' '}
                      <span style={{ fontWeight: 600 }}>
                        without requesting an extension
                      </span>
                      , making the new deadline{' '}
                      <span style={{ fontWeight: 600 }}>June 15, 2026</span>.
                      However, if more time is needed, filing{' '}
                      <span style={{ fontWeight: 600 }}>Form 4868</span> can
                      provide an{' '}
                      <span style={{ fontWeight: 600 }}>extra four months</span>
                      , giving taxpayers until
                      <span style={{ fontWeight: 600 }}> October 15, 2026</span>
                      , to file.
                    </Typography>
                    <Alert
                      severity="warning"
                      icon={false}
                      sx={{
                        mb: 5,
                        mt: 0,
                        borderLeft: '3px solid rgb(102, 60, 0)',
                      }}
                    >
                      <b> Note:</b> Filing Form 4868{' '}
                      <span style={{ fontWeight: 600 }}>
                        only extends the tax return filing deadline
                      </span>
                      —it{' '}
                      <span style={{ fontWeight: 600 }}>
                        does not extend the deadline to pay taxes owed
                      </span>
                      . Any unpaid tax will incur interest and possible
                      penalties.
                    </Alert>
                  </Box>
                  <Box
                    sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }}
                  />
                </Stack>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who Can File Form 4868?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Any U.S. citizen or resident unable to file their income tax
                  return by the deadline can submit Form 4868 to request an
                  extension.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  This extension applies to the following{' '}
                  <span style={{ fontWeight: 600 }}>
                    federal income tax returns:
                  </span>
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Form 1040</span> – U.S.
                      Individual Tax Return
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Form 1040-SR</span> –
                      U.S. Tax Return for Seniors
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Form 1040-NR</span> –
                      U.S. Nonresident Alien Income Tax Return
                    </ListItemText>
                  </ListItem>
                  {/* <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Form 1040-PR</span> –
                      Self-Employment Tax Return
                    </ListItemText>
                  </ListItem> */}
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Form 1040-SS</span> –
                      U.S. Self-Employment Tax Return
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is Form 4868 Due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For the <span style={{ fontWeight: 600 }}>2025 tax year</span>
                  , the{' '}
                  <Link
                    href="/resources/tax-extension/form-4868-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 4868 due date
                  </Link>{' '}
                  is <span style={{ fontWeight: 600 }}>April 15, 2026</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Individual taxpayers who want an extension must file 4868 Form
                  on or before the due date for the actual tax return to be
                  filed. However, if the due date falls on weekends or federal
                  holidays, then the filing deadline will be the next business
                  day.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Filing Form 4868 for the 2025 tax year will extend the filing
                  deadline to
                  <span style={{ fontWeight: 600 }}> October 15, 2026</span>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Taxpayers who are out of the country on the original due date
                  get an automatic 2-month extension until June 15, 2026. They
                  can file Form 4868 to receive an additional 4-month extension.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Extend Your Income Tax Deadline in Minutes!
                      </Typography>
                      <Typography variant="body2">
                        File Form 4868 electronically with TaxZerone and get
                        extra time to finalize your tax return. Just 3 simple
                        steps, done in minutes.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 4868 penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If taxpayers fail to file their income taxes on time or fail
                  to pay taxes, they may face penalties from the IRS:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 15,
                            }}
                          >
                            Late Filing Penalty
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              fontWeight: 600,
                              fontSize: 15,
                            }}
                          >
                            Late payment Penalty{' '}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            The penalty is{' '}
                            <span style={{ fontWeight: 600 }}>
                              5% of the unpaid tax per month
                            </span>
                            .
                          </TableCell>
                          <TableCell>
                            The penalty is{' '}
                            <span style={{ fontWeight: 600 }}>
                              0.5 of 1% of the unpaid tax per month.
                            </span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            The maximum penalty is{' '}
                            <span style={{ fontWeight: 600 }}>25%.</span>
                          </TableCell>
                          <TableCell>
                            The maximum penalty is{' '}
                            <span style={{ fontWeight: 600 }}>25%.</span>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            If the return is over{' '}
                            <span style={{ fontWeight: 600 }}>
                              60 days late
                            </span>
                            , the minimum penalty is{' '}
                            <span style={{ fontWeight: 600 }}>$525</span>{' '}
                            (adjusted for inflation) or the balance of the tax
                            due, whichever is smaller.{' '}
                          </TableCell>
                          <TableCell> </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontStyle: 'italic', mb: 2.5 }}
                >
                  Filing an extension with 4868 irs form gives you extra time to
                  <span style={{ fontWeight: 600 }}> file</span> your return,
                  but{' '}
                  <span style={{ fontWeight: 600 }}>not extra time to pay</span>
                  . Ensure timely payments to avoid penalties and interest.{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 4868 Instructions - How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To fill out the extension request accurately, follow the Form
                  4868 instructions below
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Part I: Identification
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4868-part1-img.webp" />
                  <source srcSet="/assets/images/f4868-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4868-part1-img.png"
                    loading="lazy"
                    alt="2025 Form 4868 – Line 1: Enter Name and full address"
                    width={445}
                    height={163}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Line 1 – <b>Name and Address </b>
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>

                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}> Name(s):</span> Enter
                      your full legal name (and spouse’s name for joint filers).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>Address:</span> Provide
                      your <span style={{ fontWeight: 600 }}>current</span>{' '}
                      mailing address.
                    </ListItemText>
                  </ListItem>
                </List>
                <picture>
                  <source srcSet="/assets/images/f4868-social-security-img.webp" />
                  <source srcSet="/assets/images/f4868-social-security-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4868-social-security-img.png"
                    loading="lazy"
                    alt="2025 Form 4868 – Line 2: Enter your social security number"
                    width={217}
                    height={42}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Line 2 -{' '}
                  <b>
                    Social Security Number (SSN) or Employer Identification
                    Number (EIN):{' '}
                  </b>
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>

                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Enter your social security number (SSN).
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Estates or trusts filing Form 1040-NR should enter their
                      EIN and write &quot;estate&quot; or &quot;trust&quot; in
                      the left margin.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Nonresidents without an SSN must apply for an ITIN using
                      Form W-7.
                    </ListItemText>
                  </ListItem>
                </List>
                <picture>
                  <source srcSet="/assets/images/f4868-spouse-img.webp" />
                  <source srcSet="/assets/images/f4868-spouse-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4868-spouse-img.png"
                    loading="lazy"
                    alt="2025 Form 4868 – Line 3: Enter the spouse’s social security number"
                    width={230}
                    height={42}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Line 3 – <b>Spouse’s SSN (If Filing Jointly) </b>
                  spouse’s SSN.
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>

                    <ListItemText
                      sx={{
                        fontWeight: 400,
                        mb: 0,
                        lineHeight: 1.5,
                      }}
                    >
                      Enter your{' '}
                      <span style={{ fontWeight: 600 }}>spouse’s SSN</span> as
                      it will appear on your joint tax return.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Part II: Individual Income Tax
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f4868-part2-img.webp" />
                  <source srcSet="/assets/images/f4868-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4868-part2-img.png"
                    loading="lazy"
                    alt="2025 Form 4868 – Part II: Enter the total tax liability, total payments, balance due and amount you are paying."
                    width={457}
                    height={122}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Line 4—<b>Estimate of Total Tax Liability for 2025</b>
                </Typography>{' '}
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>total tax liability</span>{' '}
                    expected for 2025, based on:{' '}
                  </li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        Form 1040, 1040-SR, or 1040-NR:
                      </span>{' '}
                      Line 24; or
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Form 1040-SS:</span>{' '}
                      Part I, Line 7 <br />
                      (If the expected tax liability is zero, enter 0.)
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Line 5—<b>Estimate of Total Payments for 2025 </b>
                </Typography>{' '}
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the{' '}
                    <span style={{ fontWeight: 600 }}>total tax liability</span>{' '}
                    you made for 2025, based on:
                  </li>
                  <ul>
                    <li>
                      <span style={{ fontWeight: 600 }}>
                        Form 1040, 1040-SR, or 1040-NR:
                      </span>{' '}
                      Line 33 (excluding Schedule 3, Line 10); or
                    </li>
                    <li>
                      <span style={{ fontWeight: 600 }}>Form 1040-SS:</span>{' '}
                      Part I, Line 12 (excluding Line 11b) <br />({' '}
                      <span style={{ fontWeight: 600 }}>Do not</span> include
                      the amount you are paying with Form 4868)
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Line 6—<b>Balance Due </b>
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the Subtract{' '}
                    <span style={{ fontWeight: 600 }}>Line 5</span> from{' '}
                    <span style={{ fontWeight: 600 }}>Line 4</span>. If{' '}
                    <span style={{ fontWeight: 600 }}>
                      Line 5 is greater than or equal to Line 4
                    </span>
                    , enter 0.
                  </li>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Line 7—<b>Amount You&apos;re Paying </b>
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter the amount you are willing to pay in this line. In
                    case you couldn’t pay the amount displayed on line 6, you
                    can still get an extension. But you should pay as much as
                    you can to limit the amount of interest you’ll owe. Also,
                    you may be charged the late payment penalty on the unpaid
                    tax from the regular due date of your return.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f4868-applicable-boxes-img.webp" />
                  <source srcSet="/assets/images/f4868-applicable-boxes-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f4868-applicable-boxes-img.png"
                    loading="lazy"
                    alt="2025 Form 4868 – Line 8 and 9: Check the applicable boxes if you are in an “Out of the country”"
                    width={458}
                    height={82}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Line 8—<b>Out of the Country </b>
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Check this box if you were{' '}
                    <span style={{ fontWeight: 600 }}>out of the U.S.</span> on
                    the original tax deadline.
                  </li>
                  <li>
                    If eligible, this may allow additional time beyond the
                    standard extension.
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 3, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If you’re out of the country and file a calendar
                  year income tax return, you can pay the tax and file your
                  return by June 15, 2026. File internal revenue service form
                  4868 and be sure to check the box on line 8 if you need an
                  additional 4 months to file your return.
                </Alert>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Line 9—<b>Form 1040-NR Filers </b>
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    Check the box in this line if you filed Form 1040-NR and
                    didn’t receive wages as an employee subject to U.S. income
                    tax withholding.
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 4868?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form 4868 can be filed with the IRS either electronically or
                  by paper filing.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Individual taxpayers who want an extension must file Form 4868
                  on or before the due date for the actual tax return to be
                  filed. However, if the due date falls on weekends or federal
                  holidays, then the filing deadline will be the next business
                  day.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Filing Form 4868 for the 2025 tax year will extend the filing
                  deadline to{' '}
                  <span style={{ fontWeight: 600 }}>October 15, 2026</span>.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Electronic filing or E-filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  E-filing extension Form 4868 provides you with a lot of
                  benefits. Even the IRS recommends electronic filing as the
                  process is quick and you will get the instant status of your
                  return. To file Form 4868 electronically, you can opt to use
                  an e-file service provider.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  TaxZerone is an IRS-authorized e-file service provider that
                  supports the e filing of Form 4868. You can simply enter the
                  required information, calculate the balance due, and transmit
                  the return to the IRS.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can also file Form 4868 with the IRS by manually filling
                  out the form and sending it via postal email. If you are paper
                  filing your extension form, make sure you are sending the
                  return earlier before the deadline, as it may take some time
                  for the return to reach the IRS
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Although there are two filing options available for taxpayers
                  to file an extension, the IRS recommends the e-filing of Form
                  4868 as it is simple, easy, and takes less time and cost
                  compared to paper filing.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Choose E-filing. Choose TaxZerone.
                      </Typography>
                      <Typography variant="body2">
                        Save your time, money, and effort by filing extension
                        Form 4868 online with TaxZerone, an IRS-authorized
                        e-file service provider.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 200, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868instructions`}
                      >
                        E-File 4868 Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to mail Form 4868?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  After filling out Form 4868, you are required to manually send
                  the return to the IRS by postal mail. The address to which you
                  need to send the returns will vary based on where you reside
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check out the{' '}
                  <Link
                    href="/resources/tax-extension/form-4868-mailing-addresses-and-filing-guide/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    mailing address for Form 4868
                  </Link>{' '}
                  based on your location.
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            IF you reside in
                            <Hidden mdDown>
                              <br />
                            </Hidden>{' '}
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            <Hidden mdDown>
                              AND you’re making a payment, send Form 4868 with
                              <br />
                            </Hidden>{' '}
                            A your payment to Internal Revenue Service
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            AND you’re not making a payment, send Form 4868 to
                            Department of the Treasury,
                            <br />
                            Internal Revenue Service Center:
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            <Hidden mdDown>
                              <br />
                            </Hidden>{' '}
                            OR simply e-file Form 4868 using
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Florida, Georgia, Louisiana, Mississippi,
                            North Carolina, South Carolina, Tennessee, Texas
                          </TableCell>
                          <TableCell>
                            P.O. Box 1302, Charlotte, NC 28201-1302
                          </TableCell>
                          <TableCell>Austin, TX 73301-0045</TableCell>
                          <TableCell>TaxZerone At just $11.99/return</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Arizona, Arkansas, New Mexico, Oklahoma
                          </TableCell>
                          <TableCell>
                            P.O. Box 931300, Louisville, KY 40293-1300
                          </TableCell>
                          <TableCell>Austin, TX 73301-0045</TableCell>
                          <TableCell>
                            E-file Form 4868 with TaxZerone in 3 simple steps
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            Connecticut, Delaware, District of Columbia,
                            Illinois, Indiana, Iowa, Kentucky, Maine, Maryland,
                            Massachusetts, Minnesota, Missouri, New Hampshire,
                            New Jersey, New York, Pennsylvania, Rhode Island,
                            Vermont, Virginia, West Virginia, Wisconsin
                          </TableCell>
                          <TableCell>
                            P.O. Box 931300, Louisville, KY 40293-1300
                          </TableCell>
                          <TableCell>Kansas City, MO 64999-0045</TableCell>
                          <TableCell>
                            Complete your 4868 e-filing process with TaxZerone
                            in less than 5 minutes
                          </TableCell>
                        </TableRow>
                        {/* <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Pennsylvania</TableCell>
                          <TableCell>
                            P.O. Box 802503, Cincinnati, OH 45280-2503
                          </TableCell>
                          <TableCell>Kansas City, MO 64999-0045</TableCell>
                          <TableCell>
                            Quickly and Securely file Form 4868 with TaxZerone
                          </TableCell>
                        </TableRow> */}
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, California, Colorado, Hawaii, Idaho, Kansas,
                            Michigan, Montana, Nebraska, Nevada, North Dakota,
                            Ohio, Oregon, South Dakota, Utah, Washington,
                            Wyoming
                          </TableCell>
                          <TableCell>
                            P.O. Box 931300, Louisville, KY 40293-1300
                          </TableCell>
                          <TableCell>Ogden, UT 84201-0045</TableCell>
                          <TableCell>
                            E-file 4868 extension with TaxZerone An
                            IRS-authorized provider
                          </TableCell>
                        </TableRow>
                        {/* <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alabama, Georgia, North Carolina, South Carolina,
                            Tennessee
                          </TableCell>
                          <TableCell>
                            P.O. Box 1302, Charlotte, NC 28201-1302
                          </TableCell>
                          <TableCell>Kansas City, MO 64999-0045</TableCell>
                          <TableCell>
                            Use TaxZerone to simplify your 4868 e-filing process
                          </TableCell>
                        </TableRow> */}
                        <TableRow sx={{}}>
                          <TableCell>
                            A foreign country, American Samoa, or Puerto Rico,
                            or are excluding income under Internal Revenue Code
                            section 933, or use an APO or FPO address, or file
                            Form 2555 or 4563, or are a dual-status alien, or
                            are a nonpermanent resident of Guam or the U.S.
                            Virgin Islands
                          </TableCell>
                          <TableCell sx={{}}>
                            P.O. Box 1303, Charlotte, NC 28201-1303
                          </TableCell>
                          <TableCell sx={{}}>
                            {' '}
                            Austin, TX 73301-0215 USA{' '}
                          </TableCell>
                          <TableCell>
                            TaxZerone supports e-filing of tax Form 4868 with
                            the IRS
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            All foreign estate and trust Form 1040-NR filers
                          </TableCell>
                          <TableCell sx={{}}>
                            P.O. Box 1303, Charlotte, NC 28201-1303 USA
                          </TableCell>
                          <TableCell sx={{}}>
                            Kansas City, MO 64999-0045 USA
                          </TableCell>
                          <TableCell>
                            File extension forms with TaxZerone and stay tax
                            compliant
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            All other Form 1040-NR and 1040-SS filers
                          </TableCell>

                          <TableCell sx={{}}>
                            P.O. Box 1303, Charlotte, NC 28201-1303 USA
                          </TableCell>
                          <TableCell sx={{}}>
                            Austin, TX 73301-0215 USA{' '}
                          </TableCell>
                          <TableCell>
                            File extension forms with TaxZerone and get instant
                            return updates{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="body2" sx={{ mt: 1, color: '#6f6c7f' }}>
                    {/* Source - Where To File a Paper Form 4868 */}
                  </Typography>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 4868?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  E-filing extension Form 4868 can be completed in 3 simple
                  steps. Before you start with the e-file process, keep the
                  information below ready.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Information required to file Form 4868 online
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Your basic information such as name, SSN, and address. If
                      filing a joint return, your spouse&apos;s name and SSN.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Tax year</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Estimate of total tax liability for the tax year
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Total tax repayment amount</ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When you have all the information, you can then follow the
                  steps below to{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-4868-personal-tax-extension-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form 4868
                  </Link>{' '}
                  using TaxZerone.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 1:</b> Select the tax year and enter the required
                  information listed above.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 2:</b> Calculate the balance due by providing
                  estimated total tax liability for the tax year
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 5,
                    lineHeight: 1.5,
                  }}
                >
                  <b>Step 3:</b> Transmit the return to the IRS.
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Do you have all the required information to file your 4868
                      extension IRS?
                    </Typography>
                    <Typography variant="body2">
                      Quickly e-file Form 4868 with TaxZerone and get up to a
                      6-month extension.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868instructions`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to pay the balance due while filing Form 4868 online?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  While completing step 2, if you have a balance due, you can
                  opt to pay the due amount using the{' '}
                  <Link
                    href="/resources/electronic-funds-withdrawal-efw/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    electronic funds withdrawal
                  </Link>{' '}
                  (EFW) option. You will need to provide your bank account
                  information, such as account number, account type, and routing
                  number.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 0,
                    lineHeight: 1.5,
                  }}
                >
                  Once the IRS processes your return, your balance due will be
                  automatically debited from your account. Make sure to maintain
                  a sufficient balance in your account.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 4868 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File 4868 extension form easily with TaxZerone at just
              <b> $11.99/return.</b> You will only have to enter the required
              information, calculate your balance due, and transmit the return
              to the IRS.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              You will also get email updates on your return status as soon as
              the IRS processes it.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to file Form 4868 online?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=4868instructions`}
              >
                E-file Form 4868 Now
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Takes 3 simple steps and less than 5 minutes
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form4868Instructions;
